const STORAGE_KEY = "cookie-consent";
const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE_S = 60 * 60 * 24 * 30 * 6; // 6 mois (recommandation CNIL)

export interface CookieConsentRecord {
  analytics: boolean;
  mapbox: boolean;
  version: number;
  decidedAt: string;
}

export function useCookieConsent() {
  const rawStored = useCookie<CookieConsentRecord | null>(STORAGE_KEY, {
    default: () => null,
    maxAge: CONSENT_MAX_AGE_S,
    sameSite: "lax",
    secure: !import.meta.dev,
    path: "/",
  });
  const modalOpen = useState<boolean>("cookie-consent:modal-open", () => false);

  const stored = computed<CookieConsentRecord | null>(() => {
    const v = rawStored.value;
    if (!v) return null;
    if (v.version !== CONSENT_VERSION) return null;
    if (!Number.isFinite(Date.parse(v.decidedAt))) return null;
    return v;
  });

  const decided = computed(() => stored.value !== null);
  const analytics = computed(() => stored.value?.analytics === true);
  const mapbox = computed(() => stored.value?.mapbox === true);

  function persist(values: { analytics: boolean; mapbox: boolean }) {
    rawStored.value = {
      analytics: values.analytics,
      mapbox: values.mapbox,
      version: CONSENT_VERSION,
      decidedAt: new Date().toISOString(),
    };
  }

  return {
    decided,
    analytics,
    mapbox,
    consent: stored,
    modalOpen,
    acceptAll: () => persist({ analytics: true, mapbox: true }),
    refuseAll: () => persist({ analytics: false, mapbox: false }),
    save: (values: { analytics: boolean; mapbox: boolean }) => persist(values),
    openModal: () => {
      modalOpen.value = true;
    },
    closeModal: () => {
      modalOpen.value = false;
    },
  };
}
